import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Loader2, TrendingUp, TrendingDown, DollarSign, Users, 
  Receipt, ArrowUpRight, ArrowDownRight, Calendar, BarChart3
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';

const TIER_COLORS = {
  STARTER: '#3B82F6',
  GROWTH: '#10B981',
  TRADER: '#F59E0B',
  BUSINESS: '#06B6D4',
  ENTERPRISE: '#EC4899',
  DEMO: '#8B5CF6'
};

const Revenue = () => {
  const { fetchWithAuth } = usePlatformAuth();
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [revenueRes, txnRes] = await Promise.all([
        fetchWithAuth('/api/platform/analytics/revenue'),
        fetchWithAuth('/api/platform/analytics/transactions')
      ]);

      if (!revenueRes.ok) throw new Error('Failed to load revenue analytics');
      if (!txnRes.ok) throw new Error('Failed to load transaction analytics');

      const revenueData = await revenueRes.json();
      const txnData = await txnRes.json();

      setAnalytics(revenueData);
      setTransactions(txnData);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (paise) => {
    if (!paise) return '₹0';
    return `₹${(paise / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </PlatformLayout>
    );
  }

  const summary = analytics?.summary || {};
  const monthlyTrend = analytics?.monthly_trend || [];
  const revenueByTier = analytics?.revenue_by_tier || [];
  const dailyTxnTrend = transactions?.daily_trend || [];

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Revenue Analytics</h1>
          <p className="text-slate-400 mt-1">Track financial performance and growth metrics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Monthly Recurring Revenue</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="mrr">
                    {summary.mrr_formatted || '₹0'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">MRR</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Annual Run Rate</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="arr">
                    {summary.arr_formatted || '₹0'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">ARR</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">This Month</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {summary.current_month_formatted || '₹0'}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {summary.growth_rate > 0 ? (
                      <ArrowUpRight className="w-4 h-4 text-green-400" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-red-400" />
                    )}
                    <span className={`text-xs ${summary.growth_rate > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {summary.growth_rate > 0 ? '+' : ''}{summary.growth_rate}% vs last month
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Year to Date</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {summary.ytd_formatted || '₹0'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{analytics?.total_invoices_ytd || 0} invoices</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Avg Revenue Per Customer</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {summary.arpc_formatted || '₹0'}
                  </p>
                </div>
                <Badge className="bg-cyan-500/10 text-cyan-400">ARPC</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">New Customers (This Month)</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {summary.new_customers_this_month || 0}
                  </p>
                </div>
                <Badge className="bg-green-500/10 text-green-400">Growth</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Churned (This Month)</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {summary.churned_this_month || 0}
                  </p>
                </div>
                <Badge className="bg-red-500/10 text-red-400">Churn</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Trend */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Revenue Trend</CardTitle>
              <CardDescription className="text-slate-400">Monthly revenue over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#9CA3AF" 
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#9CA3AF" 
                      fontSize={12}
                      tickLine={false}
                      tickFormatter={(value) => `₹${(value / 100000).toFixed(0)}L`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1E293B', 
                        border: '1px solid #334155',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), 'Revenue']}
                      labelStyle={{ color: '#F1F5F9' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, fill: '#60A5FA' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Revenue by Tier */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Revenue by Plan</CardTitle>
              <CardDescription className="text-slate-400">Year to date breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                {revenueByTier.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={revenueByTier}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="revenue"
                        nameKey="tier"
                        label={({ tier, percent }) => `${tier} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {revenueByTier.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={TIER_COLORS[entry.tier] || '#6B7280'} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1E293B', 
                          border: '1px solid #334155',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [formatCurrency(value), 'Revenue']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    No revenue data available
                  </div>
                )}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 justify-center">
                {revenueByTier.map((entry) => (
                  <div key={entry.tier} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: TIER_COLORS[entry.tier] || '#6B7280' }}
                    />
                    <span className="text-sm text-slate-300">{entry.tier}</span>
                    <span className="text-sm text-slate-500">{entry.formatted}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction Analytics */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Transaction Volume</CardTitle>
                <CardDescription className="text-slate-400">Daily transaction count (last 30 days)</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-slate-400">This Month</p>
                  <p className="text-xl font-bold text-white">{transactions?.total_this_month || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">Volume</p>
                  <p className="text-xl font-bold text-green-400">{transactions?.volume_formatted || '₹0'}</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyTxnTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9CA3AF" 
                    fontSize={11}
                    tickLine={false}
                    interval={4}
                  />
                  <YAxis 
                    stroke="#9CA3AF" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1E293B', 
                      border: '1px solid #334155',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [value, 'Transactions']}
                    labelStyle={{ color: '#F1F5F9' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#8B5CF6" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Transaction Status Breakdown */}
            {transactions?.by_status && Object.keys(transactions.by_status).length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-700">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Transaction Status (This Month)</h4>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(transactions.by_status).map(([status, count]) => (
                    <div key={status} className="flex items-center gap-2">
                      <Badge className={
                        status === 'COMPLETED' ? 'bg-green-500/10 text-green-400' :
                        status === 'PENDING' ? 'bg-yellow-500/10 text-yellow-400' :
                        status === 'FAILED' ? 'bg-red-500/10 text-red-400' :
                        'bg-slate-600 text-slate-300'
                      }>
                        {status}: {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  );
};

export default Revenue;
